import axios from 'axios';
import { PaymentSubmitRequest } from '../types/customer';
import { errorLogger } from './errorLogger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://kenluk-backend-production.up.railway.app/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export interface NormalizedApiError {
  statusCode: number;
  message: string;
  requestId?: string;
  path?: string;
  method?: string;
}

export const normalizeApiError = (error: any): NormalizedApiError => {
  const statusCode = error?.response?.status || 0;
  const responseData = error?.response?.data || {};
  const requestId =
    error?.response?.headers?.['x-request-id'] || responseData?.requestId;
  const message =
    responseData?.message ||
    error?.message ||
    'An unexpected error occurred. Please try again.';

  return {
    statusCode,
    message,
    requestId,
    path: error?.config?.url,
    method: error?.config?.method?.toUpperCase?.(),
  };
};

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalizedError = normalizeApiError(error);
    const requestUrl = error.config?.url || '';
    const isVerifyRequest = requestUrl.includes('/auth/verify');

    errorLogger.logError('API request failed', error, {
      requestId: normalizedError.requestId,
      statusCode: normalizedError.statusCode,
      path: normalizedError.path,
      method: normalizedError.method,
      serverMessage: normalizedError.message,
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('app:api-error', {
          detail: normalizedError,
        })
      );
    }

    if (error.response?.status === 401 && !isVerifyRequest) {
      sessionStorage.clear();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  name: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user?: {
      id: string;
      name: string;
      email: string;
      isVerified: boolean;
      role: 'admin' | 'user';
      accountType?: 'customer' | 'company';
      accountStatus: 'pending' | 'approved' | 'rejected';
      documentsSubmitted?: boolean;
      twoFactorEnabled?: boolean;
    };
    tokens?: {
      accessToken: string;
      refreshToken: string;
    };
    twoFactorRequired?: boolean;
  };
}

export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  adminLogin: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/admin/login', credentials);
    return response.data;
  },

  signup: async (data: SignupData): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/register', data);
    return response.data;
  },

  verifyEmail: async (data: { email: string; otp: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/verify-email', data);
    return response.data;
  },

  verifyTwoFactor: async (data: { email: string; twoFactorCode: string }): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/verify-2fa', data);
    return response.data;
  },

  resendTwoFactorCode: async (data: { email: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/resend-2fa', data);
    return response.data;
  },

  resendVerificationCode: async (data: { email: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/resend-verification', data);
    return response.data;
  },

  forgotPassword: async (data: { email: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/forgot-password', data);
    return response.data;
  },

  resetPassword: async (data: { token: string; password: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(`/auth/reset-password/${data.token}`, { password: data.password });
    return response.data;
  },

  verify: async (): Promise<{ success: boolean; message: string; data: { user: NonNullable<AuthResponse['data']['user']> } }> => {
    const response = await api.get<{ success: boolean; message: string; data: { user: NonNullable<AuthResponse['data']['user']> } }>('/auth/verify');
    return response.data;
  },
};

export const contactApi = {
  submitMessage: async (data: { name: string; email: string; subject: string; message: string }): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/contact', data);
    return response.data;
  },
};

export const kycApi = {
  getUploadUrl: async (data: { documentType: string; fileName: string; fileSize: number; mimeType: string; isReupload?: boolean }): Promise<{ uploadUrl: string; s3Key: string; bucketName: string; expiresIn: number }> => {
    const response = await api.post('/kyc/upload-url', data);
    return response.data.data;
  },

  confirmUpload: async (data: { s3Key: string; bucketName: string; documentType: string; fileName: string; fileSize: number; mimeType: string }): Promise<{ success: boolean; message: string; data: { documentId: string; status: string; uploadedAt: string } }> => {
    const response = await api.post('/kyc/confirm-upload', data);
    return response.data;
  },

  getMyDocuments: async (): Promise<{ documents: import('../types/customer').KycDocument[] }> => {
    const response = await api.get('/kyc/my-documents');
    return { documents: response.data.data };
  },

  reviewDocument: async (documentId: string, data: { action: 'approve' | 'reject'; rejectionReason?: string }): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/kyc/${documentId}/review`, data);
    return response.data;
  },
};

export const paymentsApi = {
  getUploadInvoiceUrl: async (data: { fileName: string; fileSize: number; mimeType: string }): Promise<{ uploadUrl: string; s3Key: string; bucketName: string; expiresIn: number }> => {
    const response = await api.post('/payments/upload-invoice-url', data);
    return response.data.data;
  },

  submitPayment: async (request: PaymentSubmitRequest): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/payments/submit-request', request);
    return response.data;
  },

  getMyPayments: async (): Promise<{ payments: import('../types/customer').PaymentRequest[] }> => {
    const response = await api.get('/payments/my-requests');
    return { payments: response.data.data };
  },

  getPaymentById: async (paymentId: string): Promise<{ payment: import('../types/customer').PaymentRequest }> => {
    const response = await api.get(`/payments/${paymentId}`);
    return { payment: response.data.data };
  },

  getPaymentInvoiceUrl: async (paymentId: string): Promise<{ invoiceUrl: string; fileName?: string }> => {
    const response = await api.get(`/payments/${paymentId}/invoice-url`);
    return response.data.data;
  },

  getAllPayments: async (): Promise<{ payments: import('../types/customer').PaymentRequest[] }> => {
    const response = await api.get('/payments/all');
    return { payments: response.data.data };
  },

  reviewPayment: async (paymentId: string, data: { action: 'approve' | 'reject'; rejectionReason?: string; manualFxRate?: number }): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/payments/${paymentId}/review`, data);
    return response.data;
  },

  actionPayment: async (paymentId: string, data: { action: string }): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/payments/${paymentId}/action`, data);
    return response.data;
  },

  uploadPaymentDocuments: async (paymentId: string, data: { documentType: string; fileName: string; fileSize: number; mimeType: string }): Promise<{ uploadUrl: string; s3Key: string; bucketName: string; expiresIn: number }> => {
    const response = await api.post(`/payments/${paymentId}/documents`, data);
    return response.data.data;
  },

  approvePayment: async (paymentId: string, manualFxRate?: number): Promise<{ message: string; data: { paymentId: string; status: string; approvedAt: string; reapStatus?: string; reapError?: string } }> => {
    const response = await api.put(`/payments/${paymentId}/review`, { action: 'approve', manualFxRate });
    return response.data;
  },

  rejectPayment: async (paymentId: string, rejectionReason: string): Promise<{ message: string; data: { paymentId: string; status: string; approvedAt: string; rejectionReason: string } }> => {
    const response = await api.put(`/payments/${paymentId}/review`, { action: 'reject', rejectionReason });
    return response.data;
  },

  debitWalletForPayment: async (paymentId: string): Promise<{ success: boolean; data: { paymentId: string; status: string; walletDebitAmount: number; newWalletBalance: number; message: string; reapStatus?: string; reapError?: string } }> => {
    const response = await api.post(`/wallet/approve-payment/${paymentId}`);
    return response.data;
  },

  retryReapSubmission: async (paymentId: string): Promise<{ success: boolean; message: string; data: { paymentId: string; status: string; reapStatus?: string; reapError?: string } }> => {
    const response = await api.put(`/payments/${paymentId}/retry-reap`, {});
    return response.data;
  },

  performReapAction: async (
    paymentId: string,
    action: 'approve' | 'accept_quote' | 'cancel',
    message?: string
  ): Promise<{ success: boolean; message: string; data: { paymentId: string; action: string; reapActionResponse: unknown; invoiceUploadResponse?: unknown; invoiceUploadError?: string } }> => {
    const response = await api.put(`/payments/${paymentId}/reap-action`, { action, message });
    return response.data;
  },

  completePayment: async (paymentId: string): Promise<{ message: string; data: { paymentId: string; status: string; completedAt: string } }> => {
    const response = await api.put(`/payments/${paymentId}/complete`, {});
    return response.data;
  },

  getPaymentReceipt: async (paymentId: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/payments/${paymentId}/receipt`);
    return response.data;
  },

  downloadPaymentReceipt: async (paymentId: string): Promise<Blob> => {
    const response = await api.get(`/payments/${paymentId}/receipt/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const ratesApi = {
  // Get exchange rates from admin-configured settings
  getUsdNgnRate: async (): Promise<{ usdToNgnRate: number; ngnToUsdRate: number; lastUpdatedBy?: any; updatedAt?: string }> => {
    const response = await api.get('/rates/usd-ngn-rate');
    return response.data.data;
  },
};

export const adminRatesApi = {
  // Get exchange rates (admin view with metadata)
  getRates: async (): Promise<{ usdToNgnRate: number; ngnToUsdRate: number; lastUpdatedBy: any; updatedAt: string; notes?: string }> => {
    const response = await api.get('/rates/admin/rates');
    return response.data.data;
  },

  // Update exchange rates (admin only)
  updateRates: async (data: { usdToNgnRate: number; ngnToUsdRate: number; notes?: string }): Promise<{ message: string; data: { usdToNgnRate: number; ngnToUsdRate: number; lastUpdatedBy: any; updatedAt: string; notes?: string } }> => {
    const response = await api.put('/rates/admin/rates', data);
    return response.data;
  }
};

export const simulationApi = {
  simulateFundAccount: async (data: { currency: string; amount: number; network: string }): Promise<{ success: boolean; message: string; data: { currency: string; amount: number; network: string; transactionId: string; balance: number } }> => {
    const response = await api.post('/simulations/fund-account', data);
    return response.data;
  },

  getAccountBalance: async (): Promise<{ success: boolean; message: string; data: any }> => {
    const response = await api.get('/simulations/account-balance');
    return response.data;
  },

  simulatePaymentLifecycle: async (paymentId: string, data: { status: string }): Promise<{ success: boolean; message: string; data: any }> => {
    const response = await api.post(`/simulations/payments/${paymentId}/lifecycle`, data);
    return response.data;
  },

  simulateTrackingLifecycle: async (paymentId: string, data: { trackingStatus: string }): Promise<{ success: boolean; message: string; data: any }> => {
    const response = await api.post(`/simulations/payments/${paymentId}/tracking`, data);
    return response.data;
  },
};

export const adminApi = {
  getPendingUsers: async (): Promise<{ users: any[] }> => {
    const response = await api.get<{ users: any[] }>('/admin/pending-users');
    return response.data;
  },

  getPendingKycSubmissions: async (): Promise<{ data: any[] }> => {
    const response = await api.get<{ data: any[] }>('/admin/pending-kyc-submissions');
    return response.data;
  },

  approveUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/admin/users/${userId}/reject`);
    return response.data;
  },

  getPendingDocuments: async (): Promise<{ data: any[]; pagination: any }> => {
    const response = await api.get('/kyc/pending');
    return { data: response.data.data, pagination: response.data.pagination };
  },

  approveDocument: async (documentId: string): Promise<{ message: string }> => {
    return kycApi.reviewDocument(documentId, { action: 'approve' });
  },

  rejectDocument: async (documentId: string, rejectionReason: string): Promise<{ message: string }> => {
    return kycApi.reviewDocument(documentId, { action: 'reject', rejectionReason });
  },

  deleteUnverifiedUsers: async (): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>('/admin/delete-unverified-users');
    return response.data;
  },

  getDashboard: async (): Promise<any> => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  getAllUsers: async (): Promise<{ users: any[] }> => {
    const response = await api.get('/admin/users');
    return response.data.data;
  },

  deleteUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.delete<{ message: string }>(`/admin/users/${userId}`);
    return response.data;
  },

  updateUser: async (userId: string, userData: any): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/admin/users/${userId}`, userData);
    return response.data;
  },

  approveAccount: async (userId: string): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/admin/users/${userId}/approve`);
    return response.data;
  },

  rejectAccount: async (userId: string, reason: string): Promise<{ message: string }> => {
    const response = await api.put<{ message: string }>(`/admin/users/${userId}/reject`, { reason });
    return response.data;
  },

  getVirtualAccounts: async (queryParams?: string): Promise<{ success: boolean; data: { virtualAccounts: any[]; pagination: any }; error?: string }> => {
    const response = await api.get(`/admin/virtual-accounts${queryParams || ''}`);
    return response.data;
  },

  cleanupVirtualAccounts: async (): Promise<{ success: boolean; message: string; data: { matched: number; modified: number } }> => {
    const response = await api.post('/admin/virtual-accounts/cleanup-stale');
    return response.data;
  },

  completeVirtualAccount: async (paymentId: string): Promise<{ success: boolean; message: string; data?: { paymentId: string; status: string } }> => {
    const response = await api.post(`/admin/virtual-accounts/${paymentId}/complete`);
    return response.data;
  },

  getFundingReceipt: async (paymentId: string): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/payments/fidelity/${paymentId}/receipt`);
    return response.data;
  },

  downloadFundingReceipt: async (paymentId: string): Promise<Blob> => {
    const response = await api.get(`/payments/fidelity/${paymentId}/receipt/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export const beneficiaryApi = {
  getMyBeneficiaries: async (): Promise<{ beneficiaries: import('../types/customer').Beneficiary[] }> => {
    const response = await api.get('/beneficiaries/my');
    return { beneficiaries: response.data.data };
  },
  saveBeneficiary: async (data: Omit<import('../types/customer').Beneficiary, '_id' | 'useCount' | 'lastUsedAt'>): Promise<{ message: string; data: import('../types/customer').Beneficiary }> => {
    const response = await api.post('/beneficiaries', data);
    return response.data;
  },
  deleteBeneficiary: async (beneficiaryId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/beneficiaries/${beneficiaryId}`);
    return response.data;
  }
};

export default api;
